const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helper to get stored token
const getStoredToken = () => localStorage.getItem('session_token');

// Generic fetch helper
export const fetchApi = async (endpoint, options = {}) => {
  const token = getStoredToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// Auth API
export const authApi = {
  getMe: () => fetchApi('/auth/me'),
  logout: () => fetchApi('/auth/logout', { method: 'POST' }),
  adminLogin: (password) => fetchApi('/auth/admin-login', {
    method: 'POST',
    body: JSON.stringify({ password })
  }),
  register: (email, password, name, subscribe_newsletter = true) => fetchApi('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name, subscribe_newsletter })
  }),
};

// Sermons API
export const sermonsApi = {
  getAll: (year, month) => {
    let url = '/sermons';
    const params = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return fetchApi(url);
  },
  search: (query, year, month) => {
    let url = '/sermons/search';
    const params = [];
    if (query) params.push(`q=${encodeURIComponent(query)}`);
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return fetchApi(url);
  },
  importYouTube: (playlistUrl) => fetchApi('/sermons/import-youtube', {
    method: 'POST',
    body: JSON.stringify({ playlist_url: playlistUrl })
  }),
  getYears: () => fetchApi('/sermons/years'),
  getMonths: (year) => fetchApi(`/sermons/months${year ? `?year=${year}` : ''}`),
  getOne: (id) => fetchApi(`/sermons/${id}`),
  create: (data) => fetchApi('/sermons', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchApi(`/sermons/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchApi(`/sermons/${id}`, { method: 'DELETE' }),
  
  // Robust chunked video upload that handles proxy limits
  uploadVideo: async (sermonId, file, onProgress) => {
    const token = getStoredToken();
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks to stay well under proxy limits
    
    console.log(`Starting chunked upload: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
    
    // Step 1: Initialize the chunked upload
    const initResponse = await fetch(`${API}/sermons/${sermonId}/upload-video/init`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, file_size: file.size })
    });
    
    if (!initResponse.ok) {
      const error = await initResponse.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to start upload');
    }
    
    const { upload_id, total_chunks } = await initResponse.json();
    console.log(`Upload initialized: ${upload_id}, ${total_chunks} chunks`);
    
    // Step 2: Upload each chunk with retry logic
    for (let chunkIndex = 0; chunkIndex < total_chunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      let success = false;
      let lastError = null;
      
      // Retry each chunk up to 5 times
      for (let attempt = 0; attempt < 5 && !success; attempt++) {
        try {
          const formData = new FormData();
          formData.append('chunk', chunk, `chunk_${chunkIndex}`);
          
          const response = await fetch(
            `${API}/sermons/${sermonId}/upload-video/chunk/${upload_id}/${chunkIndex}`,
            {
              method: 'POST',
              credentials: 'include',
              headers: headers,
              body: formData
            }
          );
          
          if (response.ok) {
            success = true;
            // Update progress (leave last 5% for completion step)
            if (onProgress) {
              const progress = Math.round(((chunkIndex + 1) / total_chunks) * 95);
              onProgress(progress);
            }
            console.log(`Chunk ${chunkIndex + 1}/${total_chunks} uploaded`);
          } else {
            const errData = await response.json().catch(() => ({}));
            lastError = errData.detail || `HTTP ${response.status}`;
            console.warn(`Chunk ${chunkIndex} attempt ${attempt + 1} failed: ${lastError}`);
          }
        } catch (err) {
          lastError = err.message;
          console.warn(`Chunk ${chunkIndex} attempt ${attempt + 1} error: ${err.message}`);
        }
        
        // Wait before retry (exponential backoff)
        if (!success && attempt < 4) {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
      
      if (!success) {
        throw new Error(`Failed to upload chunk ${chunkIndex + 1}/${total_chunks}: ${lastError}`);
      }
    }
    
    // Step 3: Complete the upload
    if (onProgress) onProgress(97);
    console.log('All chunks uploaded, completing...');
    
    const completeResponse = await fetch(
      `${API}/sermons/${sermonId}/upload-video/complete/${upload_id}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: headers
      }
    );
    
    if (!completeResponse.ok) {
      const error = await completeResponse.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to complete upload');
    }
    
    if (onProgress) onProgress(100);
    console.log('Upload completed successfully');
    return completeResponse.json();
  },
  
  uploadThumbnail: async (sermonId, file) => {
    const token = getStoredToken();
    const formData = new FormData();
    formData.append('thumbnail', file);
    
    const response = await fetch(`${API}/sermons/${sermonId}/upload-thumbnail`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    
    return response.json();
  },
};

// Announcements API
export const announcementsApi = {
  getAll: (activeOnly = true) => fetchApi(`/announcements?active_only=${activeOnly}`),
  create: (data) => fetchApi('/announcements', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchApi(`/announcements/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchApi(`/announcements/${id}`, { method: 'DELETE' }),
};

// Events API
export const eventsApi = {
  getAll: (upcomingOnly = true) => fetchApi(`/events?upcoming_only=${upcomingOnly}`),
  create: (data) => fetchApi('/events', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchApi(`/events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchApi(`/events/${id}`, { method: 'DELETE' }),
};

// Staff API
export const staffApi = {
  getAll: () => fetchApi('/staff'),
  create: (data) => fetchApi('/staff', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchApi(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchApi(`/staff/${id}`, { method: 'DELETE' }),
};

// Prayer Requests API
export const prayerApi = {
  getAll: (publicOnly = true) => fetchApi(`/prayer-requests?public_only=${publicOnly}`),
  create: (data) => fetchApi('/prayer-requests', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchApi(`/prayer-requests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchApi(`/prayer-requests/${id}`, { method: 'DELETE' }),
};

// Members API
export const membersApi = {
  getAll: () => fetchApi('/members'),
  create: (data) => fetchApi('/members', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchApi(`/members/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchApi(`/members/${id}`, { method: 'DELETE' }),
};

// Galleries API
export const galleriesApi = {
  getAll: () => fetchApi('/galleries'),
  getOne: (id) => fetchApi(`/galleries/${id}`),
  create: (data) => fetchApi('/galleries', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchApi(`/galleries/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => fetchApi(`/galleries/${id}`, { method: 'DELETE' }),
  uploadPhoto: (galleryId, data) => fetchApi(`/galleries/${galleryId}/photos`, { method: 'POST', body: JSON.stringify(data) }),
  deletePhoto: (photoId) => fetchApi(`/photos/${photoId}`, { method: 'DELETE' }),
  approvePhoto: (photoId) => fetchApi(`/photos/${photoId}/approve`, { method: 'PUT' }),
};

// Newsletter API
export const newsletterApi = {
  getSubscribers: () => fetchApi('/newsletter/subscribers'),
  subscribe: (data) => fetchApi('/newsletter/subscribe', { method: 'POST', body: JSON.stringify(data) }),
  unsubscribe: (email) => fetchApi('/newsletter/unsubscribe', { method: 'POST', body: JSON.stringify({ email }) }),
  exportCsv: () => `${API}/newsletter/export`,
  send: (data) => fetchApi('/newsletter/send', { method: 'POST', body: JSON.stringify(data) }),
  getHistory: () => fetchApi('/newsletter/history'),
};

// Contact API
export const contactApi = {
  getMessages: () => fetchApi('/contact/messages'),
  submit: (data) => fetchApi('/contact', { method: 'POST', body: JSON.stringify(data) }),
  markRead: (id) => fetchApi(`/contact/messages/${id}/read`, { method: 'PUT' }),
  delete: (id) => fetchApi(`/contact/messages/${id}`, { method: 'DELETE' }),
};

// Settings API
export const settingsApi = {
  get: () => fetchApi('/settings'),
  update: (data) => fetchApi('/settings', { method: 'PUT', body: JSON.stringify(data) }),
  getCustomFields: () => fetchApi('/settings/custom-fields'),
  addCustomField: (data) => fetchApi('/settings/custom-fields', { method: 'POST', body: JSON.stringify(data) }),
  deleteCustomField: (fieldName) => fetchApi(`/settings/custom-fields/${fieldName}`, { method: 'DELETE' }),
};

// Users API
export const usersApi = {
  getAll: () => fetchApi('/users'),
  updateRole: (userId, role) => fetchApi(`/users/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
  createAdmin: (data) => fetchApi('/users/create-admin', { method: 'POST', body: JSON.stringify(data) }),
  updatePassword: (userId, password) => fetchApi(`/users/${userId}/password`, { method: 'PUT', body: JSON.stringify({ password }) }),
  updatePosition: (userId, position) => fetchApi(`/users/${userId}/position`, { method: 'PUT', body: JSON.stringify({ position }) }),
  updateProfile: (userId, data) => fetchApi(`/users/${userId}/profile`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (userId) => fetchApi(`/users/${userId}`, { method: 'DELETE' }),
};

// Profile API
export const profileApi = {
  get: () => fetchApi('/auth/profile'),
  update: (data) => fetchApi('/auth/profile', { method: 'PUT', body: JSON.stringify(data) }),
};
